import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createInsurancePolicy,
  deleteInsurancePolicy,
  getCoverageGap,
  listInsurancePolicies,
  type CoverageEvaluation,
  type InsurancePolicy,
  type PolicyType,
  type PremiumFrequency,
} from "../lib/api-modules/insurance.js";
import {
  Button,
  Card,
  EmptyState,
  NumberField,
  DateField,
  Pill,
  ProgressBar,
  SectionHeader,
  SelectField,
  StatCard,
  Tab,
  Tabs,
  TextField,
} from "../components/design-system/index.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { formatCentsCurrency } from "../lib/util/money.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /insurance — Phase 10 (2026-05-08).
 *
 * Three tabs:
 *   1. Policies   — CRUD list filtered by policy_type.
 *   2. Coverage gap — term life / disability / umbrella tiles with
 *                     status badges + recommended additional coverage.
 *   3. Renewals   — 90-day expiration window list.
 *
 * Both themes (light + dark) supported per the App Shell interstitial
 * lock.
 */

type ActiveTab = "policies" | "coverage-gap" | "renewals";

const POLICY_TYPE_OPTIONS: PolicyType[] = [
  "term_life",
  "whole_life",
  "universal_life",
  "disability_short",
  "disability_long",
  "umbrella",
  "auto",
  "homeowners",
  "ho6_coop",
  "renters",
  "health",
  "dental",
  "vision",
  "long_term_care",
  "critical_illness",
  "accident",
  "pet",
  "travel",
  "identity_theft",
  "cyber",
  "jewelry_rider",
  "art_collectibles",
  "other",
];

const PREMIUM_FREQ_OPTIONS: PremiumFrequency[] = [
  "monthly",
  "quarterly",
  "semi_annual",
  "annual",
  "one_time",
];

function policyTypeLabel(t: PolicyType): string {
  return t
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/Ho6 Coop/g, "HO-6 (co-op)");
}

export function InsurancePage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("policies");
  return (
    <div className="mx-auto max-w-5xl p-6">
      <SectionHeader
        title="Insurance"
        sub="Track every household policy in one place. Coverage-gap analysis flags under-insured categories; the renewals tab surfaces lapses before they happen."
      />
      <Tabs
        value={activeTab}
        onChange={(v) => setActiveTab(v as ActiveTab)}
        ariaLabel="Insurance tabs"
      >
        <Tab value="policies" label="Policies" />
        <Tab value="coverage-gap" label="Coverage gap" />
        <Tab value="renewals" label="Renewals" />
      </Tabs>
      <div className="mt-6">
        {activeTab === "policies" && <PoliciesTab />}
        {activeTab === "coverage-gap" && <CoverageGapTab />}
        {activeTab === "renewals" && <RenewalsTab />}
      </div>
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.insurance} />
      </div>
    </div>
  );
}

// === Policies tab ========================================================

function PoliciesTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, error, isLoading } = useQuery({
    queryKey: ["insurance-policies"],
    queryFn: listInsurancePolicies,
  });
  const [filter, setFilter] = useState<PolicyType | "">("");
  const [showAdd, setShowAdd] = useState(false);
  const deleteM = useMutation({
    mutationFn: deleteInsurancePolicy,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["insurance-policies"] });
      await queryClient.invalidateQueries({ queryKey: ["insurance-coverage-gap"] });
      toast.show("Policy deleted");
    },
    onError: (err) => toast.show((err).message),
  });
  const filtered = useMemo(() => {
    if (!data?.policies) return [];
    if (!filter) return data.policies;
    return data.policies.filter((p) => p.policy_type === filter);
  }, [data?.policies, filter]);

  if (isLoading) return <p className="text-sm">Loading policies…</p>;
  if (error) return <p className="text-sm text-[var(--color-danger)]">{error.message}</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <SelectField
          label="Filter by type"
          value={filter}
          onChange={(e) => setFilter(e.target.value as PolicyType | "")}
        >
          <option value="">All types</option>
          {POLICY_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {policyTypeLabel(t)}
            </option>
          ))}
        </SelectField>
        <Button onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? "Cancel" : "Add policy"}
        </Button>
      </div>
      {showAdd && (
        <AddPolicyForm
          onCreated={() => setShowAdd(false)}
        />
      )}
      {filtered.length === 0 ? (
        <EmptyState
          title={filter ? "No policies of that type" : "No policies tracked yet"}
          description="Add a policy to start tracking premiums + beneficiaries + renewal dates."
        />
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <PolicyRow
              key={p.policy_id}
              policy={p}
              onDelete={() => deleteM.mutate(p.policy_id)}
              isDeleting={deleteM.isPending && deleteM.variables === p.policy_id}
            />
          ))}
        </div>
      )}
      <Toast message={toast.message} />
    </div>
  );
}

function PolicyRow({
  policy,
  onDelete,
  isDeleting,
}: {
  policy: InsurancePolicy;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  return (
    <Card className="flex flex-wrap items-center gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-[var(--color-text)]">
            {policy.carrier} — {policyTypeLabel(policy.policy_type)}
          </span>
          <Pill tone={policy.policy_status === "active" ? "success" : "warning"}>
            {policy.policy_status}
          </Pill>
        </div>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Effective {policy.effective_date}
          {policy.expiration_date ? ` · expires ${policy.expiration_date}` : ""}
          {policy.coverage_amount_cents
            ? ` · coverage ${formatCentsCurrency(policy.coverage_amount_cents)}`
            : ""}
          {policy.premium_cents && policy.premium_frequency
            ? ` · premium ${formatCentsCurrency(policy.premium_cents)}/${policy.premium_frequency}`
            : ""}
        </p>
      </div>
      <ConfirmButton
        label="Delete"
        confirmLabel="Confirm delete"
        onConfirm={onDelete}
        isPending={isDeleting}
      />
    </Card>
  );
}

function AddPolicyForm({ onCreated }: { onCreated: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [policyType, setPolicyType] = useState<PolicyType>("term_life");
  const [carrier, setCarrier] = useState("");
  const [coverageDollars, setCoverageDollars] = useState("");
  const [monthlyBenefitDollars, setMonthlyBenefitDollars] = useState("");
  const [premiumDollars, setPremiumDollars] = useState("");
  const [premiumFreq, setPremiumFreq] = useState<PremiumFrequency | "">("");
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [expirationDate, setExpirationDate] = useState("");
  const create = useMutation({
    mutationFn: createInsurancePolicy,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["insurance-policies"] });
      await queryClient.invalidateQueries({ queryKey: ["insurance-coverage-gap"] });
      toast.show("Policy added");
      onCreated();
    },
    onError: (err) => toast.show((err).message),
  });
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!carrier) {
      toast.show("Carrier is required");
      return;
    }
    create.mutate({
      policy_type: policyType,
      carrier,
      effective_date: effectiveDate,
      ...(expirationDate ? { expiration_date: expirationDate } : {}),
      ...(coverageDollars
        ? { coverage_amount_cents: Math.round(parseFloat(coverageDollars) * 100) }
        : {}),
      ...(monthlyBenefitDollars
        ? {
            monthly_benefit_cents: Math.round(
              parseFloat(monthlyBenefitDollars) * 100,
            ),
          }
        : {}),
      ...(premiumDollars && premiumFreq
        ? {
            premium_cents: Math.round(parseFloat(premiumDollars) * 100),
            premium_frequency: premiumFreq,
          }
        : {}),
    });
  };
  const isLifeLike = policyType === "term_life" || policyType === "whole_life" || policyType === "universal_life";
  const isDisability = policyType === "disability_short" || policyType === "disability_long";
  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-2">
        <SelectField
          label="Policy type"
          value={policyType}
          onChange={(e) => setPolicyType(e.target.value as PolicyType)}
        >
          {POLICY_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {policyTypeLabel(t)}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Carrier"
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          placeholder="e.g., Northwestern Mutual"
        />
        {!isDisability && (
          <NumberField
            label="Coverage amount ($)"
            value={coverageDollars}
            onChange={(e) => setCoverageDollars(e.target.value)}
            min={0}
            step="1000"
          />
        )}
        {isDisability && (
          <NumberField
            label="Monthly benefit ($)"
            value={monthlyBenefitDollars}
            onChange={(e) => setMonthlyBenefitDollars(e.target.value)}
            min={0}
            step="100"
          />
        )}
        <NumberField
          label="Premium ($)"
          value={premiumDollars}
          onChange={(e) => setPremiumDollars(e.target.value)}
          min={0}
          step="1"
        />
        <SelectField
          label="Premium frequency"
          value={premiumFreq}
          onChange={(e) => setPremiumFreq(e.target.value as PremiumFrequency)}
        >
          <option value="">—</option>
          {PREMIUM_FREQ_OPTIONS.map((f) => (
            <option key={f} value={f}>
              {f.replace("_", " ")}
            </option>
          ))}
        </SelectField>
        <DateField
          label="Effective date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
        />
        <DateField
          label="Expiration date (optional)"
          value={expirationDate}
          onChange={(e) => setExpirationDate(e.target.value)}
        />
        <div className="md:col-span-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Save policy"}
          </Button>
          {isLifeLike || isDisability ? (
            <p className="mt-2 text-xs text-[var(--color-muted)]">
              Life + disability policies count toward the coverage-gap
              analysis. Beneficiaries can be edited after creation.
            </p>
          ) : null}
        </div>
      </form>
      <Toast message={toast.message} />
    </Card>
  );
}

// === Coverage gap tab ====================================================

function CoverageGapTab() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["insurance-coverage-gap"],
    queryFn: getCoverageGap,
  });
  if (isLoading) return <p className="text-sm">Calculating coverage gap…</p>;
  if (error) return <p className="text-sm text-[var(--color-danger)]">{error.message}</p>;
  if (!data) return null;
  const noIncome = data.ttm_income_cents <= 0;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          label="TTM income"
          value={formatCentsCurrency(data.ttm_income_cents)}
          sub="Last 365 days of paychecks + RSU vests + bonuses + side income"
        />
        <StatCard
          label="Net worth (latest snapshot)"
          value={formatCentsCurrency(data.net_worth_cents)}
          sub={
            data.latest_net_worth_date
              ? `As of ${data.latest_net_worth_date}`
              : "No snapshot yet"
          }
        />
        <StatCard
          label="Active coverage categories"
          value={[
            data.term_life.current_coverage_cents > 0 ? "Term" : null,
            data.disability.current_monthly_benefit_cents > 0 ? "Disability" : null,
            data.umbrella.current_coverage_cents > 0 ? "Umbrella" : null,
          ]
            .filter(Boolean)
            .join(" · ") || "None"}
        />
      </div>
      {noIncome && (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            Coverage gap analysis needs at least one income event in the last
            365 days. Add paycheck or RSU vest events on the{" "}
            <a href="/income" className="text-[var(--color-accent)] underline">Income page</a>.
          </p>
        </Card>
      )}
      <CoverageTile
        title="Term life"
        rule="10× annual income (MetLife / III)"
        evalResult={data.term_life}
        currentLabel="Current coverage"
        currentValue={formatCentsCurrency(data.term_life.current_coverage_cents)}
        targetLabel="Target (10× income)"
        targetValue={formatCentsCurrency(data.term_life.target_cents)}
      />
      <CoverageTile
        title="Disability"
        rule="60% monthly income replacement (Council for Disability Awareness / III)"
        evalResult={data.disability}
        currentLabel="Current monthly benefit"
        currentValue={formatCentsCurrency(
          data.disability.current_monthly_benefit_cents,
        )}
        targetLabel="Target monthly benefit"
        targetValue={formatCentsCurrency(data.disability.target_cents)}
        multipleAsPercent
      />
      <CoverageTile
        title="Umbrella"
        rule="1× net worth minimum (III / Bankrate)"
        evalResult={data.umbrella}
        currentLabel="Current coverage"
        currentValue={formatCentsCurrency(data.umbrella.current_coverage_cents)}
        targetLabel="Target (1× net worth)"
        targetValue={formatCentsCurrency(data.umbrella.target_cents)}
      />
    </div>
  );
}

function CoverageTile({
  title,
  rule,
  evalResult,
  currentLabel,
  currentValue,
  targetLabel,
  targetValue,
  multipleAsPercent,
}: {
  title: string;
  rule: string;
  evalResult: CoverageEvaluation;
  currentLabel: string;
  currentValue: string;
  targetLabel: string;
  targetValue: string;
  multipleAsPercent?: boolean;
}) {
  const tone =
    evalResult.status === "adequate"
      ? "success"
      : evalResult.status === "borderline"
        ? "warning"
        : "danger";
  const progressPct = Math.min(100, (evalResult.multiple / (multipleAsPercent ? 0.6 : 10)) * 100);
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-[var(--color-text)]">{title}</h3>
            <Pill tone={tone}>{evalResult.status}</Pill>
          </div>
          <p className="mt-1 text-xs text-[var(--color-muted)]">Rule of thumb: {rule}</p>
        </div>
        <div className="text-right">
          <p className="font-mono text-lg tabular-nums text-[var(--color-text)]">
            {multipleAsPercent
              ? `${(evalResult.multiple * 100).toFixed(0)}%`
              : `${evalResult.multiple.toFixed(1)}×`}
          </p>
          <p className="text-xs text-[var(--color-muted)]">
            {multipleAsPercent ? "of monthly income" : "of annual income"}
          </p>
        </div>
      </div>
      <div className="mt-4">
        <ProgressBar
          value={progressPct}
          max={100}
          tone={tone === "success" ? "success" : tone === "warning" ? "warning" : "danger"}
        />
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <div>
          <p className="text-xs text-[var(--color-muted)]">{currentLabel}</p>
          <p className="font-mono tabular-nums text-[var(--color-text)]">{currentValue}</p>
        </div>
        <div>
          <p className="text-xs text-[var(--color-muted)]">{targetLabel}</p>
          <p className="font-mono tabular-nums text-[var(--color-text)]">{targetValue}</p>
        </div>
        <div>
          <p className="text-xs text-[var(--color-muted)]">Recommended additional</p>
          <p className="font-mono tabular-nums text-[var(--color-text)]">
            {evalResult.recommended_additional_cents > 0
              ? formatCentsCurrency(evalResult.recommended_additional_cents)
              : "—"}
          </p>
        </div>
      </div>
    </Card>
  );
}

// === Renewals tab ========================================================

function RenewalsTab() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["insurance-policies"],
    queryFn: listInsurancePolicies,
  });
  const today = new Date().toISOString().slice(0, 10);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() + 90);
  const cutoffIso = cutoff.toISOString().slice(0, 10);
  const expiring = useMemo(() => {
    if (!data?.policies) return [];
    return data.policies
      .filter(
        (p) =>
          p.expiration_date &&
          p.expiration_date >= today &&
          p.expiration_date <= cutoffIso,
      )
      .sort((a, b) =>
        (a.expiration_date ?? "").localeCompare(b.expiration_date ?? ""),
      );
  }, [data?.policies, today, cutoffIso]);

  if (isLoading) return <p className="text-sm">Loading renewals…</p>;
  if (error) return <p className="text-sm text-[var(--color-danger)]">{error.message}</p>;
  if (expiring.length === 0) {
    return (
      <EmptyState
        title="No upcoming renewals"
        description="Nothing expiring in the next 90 days. Check back as policies near their renewal dates."
      />
    );
  }
  return (
    <div className="space-y-2">
      {expiring.map((p) => (
        <Card key={p.policy_id}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-medium text-[var(--color-text)]">
                {p.carrier} — {policyTypeLabel(p.policy_type)}
              </p>
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Expires {p.expiration_date}
              </p>
            </div>
            <Pill tone="warning">Renewal due</Pill>
          </div>
        </Card>
      ))}
    </div>
  );
}
